package org.teddapp.project.ui.notes.ink

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.graphics.StrokeJoin
import androidx.compose.ui.graphics.drawscope.DrawScope
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.unit.dp
import org.teddapp.project.ui.TeddTokens
import kotlin.math.sqrt

/** L'opacité du surligneur : on lit à travers. */
internal const val HIGHLIGHTER_ALPHA = 0.35f

/** Ce que pèse la pression dans l'épaisseur : à fond, l'épaisseur pleine ; effleuré, 35 %. */
private fun radius(width: Float, p: Float) = width / 2f * (0.35f + 0.65f * p)

/**
 * Le contour d'un trait de stylo, en pixels : deux bords décalés de part et d'autre
 * du tracé selon la pression, reliés en courbes. Rempli d'une seule couleur, il donne
 * l'épaisseur qui varie d'un vrai stylo. Les bouts ronds sont posés à part ([drawStroke]) :
 * ajoutés au contour, ils pourraient tourner dans l'autre sens et y faire un trou.
 */
internal fun penOutline(points: List<InkPoint>, width: Float, scale: Float): Path {
    val path = Path()
    if (points.isEmpty()) return path
    val n = points.size
    fun px(i: Int) = points[i].x * scale
    fun py(i: Int) = points[i].y * scale
    fun r(i: Int) = radius(width, points[i].p) * scale
    if (n == 1) {
        path.addOval(androidx.compose.ui.geometry.Rect(Offset(px(0), py(0)), r(0)))
        return path
    }
    val left = ArrayList<Offset>(n)
    val right = ArrayList<Offset>(n)
    for (i in 0 until n) {
        // La normale suit la direction entre le point d'avant et celui d'après : le bord
        // tourne en douceur, sans pli à chaque échantillon.
        val a = maxOf(0, i - 1)
        val b = minOf(n - 1, i + 1)
        var dx = px(b) - px(a)
        var dy = py(b) - py(a)
        val len = sqrt(dx * dx + dy * dy)
        if (len < 1e-3f) { dx = 1f; dy = 0f } else { dx /= len; dy /= len }
        val rr = r(i)
        left += Offset(px(i) - dy * rr, py(i) + dx * rr)
        right += Offset(px(i) + dy * rr, py(i) - dx * rr)
    }
    fun curve(side: List<Offset>, first: Boolean) {
        if (first) path.moveTo(side[0].x, side[0].y) else path.lineTo(side[0].x, side[0].y)
        for (i in 1 until side.size) {
            val m = (side[i - 1] + side[i]) / 2f
            path.quadraticTo(side[i - 1].x, side[i - 1].y, m.x, m.y)
        }
        path.lineTo(side.last().x, side.last().y)
    }
    curve(left, first = true)
    curve(right.asReversed(), first = false)
    path.close()
    return path
}

/** Le tracé d'un trait de surligneur : une ligne lissée, d'épaisseur fixe. */
internal fun centerLine(points: List<InkPoint>, scale: Float): Path {
    val path = Path()
    if (points.isEmpty()) return path
    path.moveTo(points[0].x * scale, points[0].y * scale)
    if (points.size == 1) { path.lineTo(points[0].x * scale + 0.1f, points[0].y * scale); return path }
    for (i in 1 until points.size) {
        val ax = points[i - 1].x * scale; val ay = points[i - 1].y * scale
        val bx = points[i].x * scale; val by = points[i].y * scale
        path.quadraticTo(ax, ay, (ax + bx) / 2f, (ay + by) / 2f)
    }
    path.lineTo(points.last().x * scale, points.last().y * scale)
    return path
}

/** La couleur d'un trait à l'écran : l'encre suit le thème, le surligneur se laisse traverser. */
internal fun strokeColor(s: InkStroke, ink: Color): Color {
    val base = if (s.color == InkStroke.INK) ink else Color(s.color.toInt())
    return if (s.tool == InkTool.Highlighter) base.copy(alpha = HIGHLIGHTER_ALPHA) else base
}

/**
 * Le calcul des traits terminés, gardé d'une image à l'autre : pendant qu'on dessine,
 * seul le trait en cours est recalculé, pas les centaines déjà posés.
 */
internal class InkPathCache {
    private var scale = -1f
    private val paths = HashMap<InkStroke, Path>()

    fun pathOf(s: InkStroke, scale: Float): Path {
        if (scale != this.scale) { paths.clear(); this.scale = scale }
        return paths.getOrPut(s) { buildPath(s, scale) }
    }

    /** Oublie les traits qui ne sont plus dans le dessin (gommés, annulés). */
    fun retain(strokes: List<InkStroke>) {
        if (paths.size > strokes.size * 2 + 16) {
            val keep = strokes.toHashSet()
            paths.keys.retainAll(keep)
        }
    }
}

internal fun buildPath(s: InkStroke, scale: Float): Path =
    if (s.tool == InkTool.Pen) penOutline(s.points, s.width, scale) else centerLine(s.points, scale)

internal fun DrawScope.drawStroke(s: InkStroke, path: Path, scale: Float, ink: Color) {
    val c = strokeColor(s, ink)
    if (s.tool == InkTool.Pen) {
        drawPath(path, c)
        if (s.points.size > 1) {
            val a = s.points.first()
            val b = s.points.last()
            drawCircle(c, radius(s.width, a.p) * scale, Offset(a.x * scale, a.y * scale))
            drawCircle(c, radius(s.width, b.p) * scale, Offset(b.x * scale, b.y * scale))
        }
    } else drawPath(path, c, style = Stroke(width = s.width * scale, cap = StrokeCap.Round, join = StrokeJoin.Round))
}

/**
 * Un dessin en lecture, à la largeur de la page. La hauteur suit les traits, pas la
 * page entière : un croquis en haut d'une grande page ne laisse pas un grand vide.
 */
@Composable
internal fun InkView(drawing: InkDrawing, modifier: Modifier = Modifier) {
    val ink = TeddTokens.Ink
    val cache = remember { InkPathCache() }
    val used = remember(drawing) {
        (drawing.strokes.maxOfOrNull { it.bounds[3] } ?: 0f).coerceAtLeast(120f) + 24f
    }
    val shape = RoundedCornerShape(TeddTokens.RMd)
    BoxWithConstraints(
        modifier = modifier
            .fillMaxWidth()
            .clip(shape)
            .background(TeddTokens.Surface, shape)
            .border(1.dp, TeddTokens.Line, shape),
    ) {
        val scale = constraints.maxWidth / InkDrawing.WIDTH
        Canvas(Modifier.fillMaxWidth().height(maxWidth * (used / InkDrawing.WIDTH))) {
            cache.retain(drawing.strokes)
            for (s in drawing.strokes) drawStroke(s, cache.pathOf(s, scale), scale, ink)
        }
    }
}
